import { supabase } from "@/lib/supabase";
import { siteUrl } from "@/lib/supabase";
import { formatDisplayDate, formatTime } from "@/lib/bookingLabels";
import { formatGbpFromPence } from "@/types/database";
import type { BookingWithClient } from "@/lib/bookingsApi";

/** UK mobile to WhatsApp format: 07700 900123 -> 447700900123. */
export function toWhatsAppNumber(phone: string | null | undefined): string {
  if (!phone) return "";
  let d = phone.replace(/[^\d+]/g, "");
  if (d.startsWith("+")) d = d.slice(1);
  if (d.startsWith("00")) d = d.slice(2);
  if (d.startsWith("0")) d = "44" + d.slice(1);
  return /^\d{10,15}$/.test(d) ? d : "";
}

export function payLinkFor(b: Pick<BookingWithClient, "pay_token">): string {
  return `${siteUrl || window.location.origin}/booking/pay/${b.pay_token}`;
}

export function whatsAppUrl(phone: string | null | undefined, text: string): string {
  const n = toWhatsAppNumber(phone);
  return `https://wa.me/${n}?text=${encodeURIComponent(text)}`;
}

export type LinkOverride = { link?: string; totalPence?: number; count?: number };

export function payMessage(b: BookingWithClient, o: LinkOverride = {}): string {
  if (o.link && o.count && o.count > 1) {
    const n = b.clients?.contact_name?.split(" ")[0] || "there";
    return `Hi ${n}, thank you for booking me for ${o.count} performances. The total is ${formatGbpFromPence(o.totalPence ?? 0)}. You can pay for them all together or one at a time, and download your invoices here: ${o.link}\n\nOlivia`;
  }
  const name = b.clients?.contact_name?.split(" ")[0] || "there";
  const when = formatDisplayDate(b.performance_date) + (b.start_time ? ` at ${formatTime(b.start_time)}` : "");
  const due = b.due_date ? ` by ${formatDisplayDate(b.due_date)}` : "";
  return `Hi ${name}, thank you for booking me for ${when}. The total is ${formatGbpFromPence(b.fee_pence)}${due}. You can pay and download your invoice here: ${payLinkFor(b)}\n\nOlivia`;
}

export function chaseMessage(b: BookingWithClient, o: LinkOverride = {}): string {
  const name = b.clients?.contact_name?.split(" ")[0] || "there";
  if (o.link && o.count && o.count > 1) return `Hi ${name}, just a friendly reminder that ${formatGbpFromPence(o.totalPence ?? 0)} for your ${o.count} performances is still outstanding. You can pay here: ${o.link}\n\nThank you, Olivia`;
  return `Hi ${name}, just a friendly reminder that payment of ${formatGbpFromPence(b.fee_pence)} for ${formatDisplayDate(b.performance_date)} is still outstanding. You can pay here: ${payLinkFor(b)}\n\nThank you, Olivia`;
}

/** Record a manual payment (bank transfer or cash) and mark the booking paid. */
export async function markPaid(b: BookingWithClient, method: "bank_transfer" | "cash", userId?: string) {
  if (!supabase) return { ok: false as const, error: "Not connected." };
  const now = new Date().toISOString();
  const { error: pErr } = await supabase.from("payments").insert({
    booking_id: b.id,
    amount_pence: b.fee_pence,
    currency: b.currency || "gbp",
    method,
    status: "succeeded",
    paid_at: now,
    recorded_by: userId ?? null,
  });
  if (pErr) return { ok: false as const, error: pErr.message };
  const { error } = await supabase.from("bookings").update({ payment_status: "paid" }).eq("id", b.id);
  if (error) return { ok: false as const, error: error.message };
  return { ok: true as const };
}

export type PastClient = {
  client_id: string;
  venue_or_client_name: string;
  contact_name: string;
  email: string;
  phone: string;
  venue_postcode: string;
  address: string;
  category: string;
  fee_pounds: string;
};

/** Each client once, with details from their latest booking. */
export async function listPastClients(): Promise<PastClient[]> {
  if (!supabase) return [];
  const { data } = await supabase
    .from("bookings")
    .select("client_id, venue_postcode, address, category, fee_pence, fuel_charge_pence, created_at, clients ( venue_or_client_name, contact_name, email, phone )")
    .order("created_at", { ascending: false })
    .limit(500);
  const seen = new Set<string>();
  const out: PastClient[] = [];
  for (const r of (data ?? []) as Array<Record<string, any>>) {
    if (seen.has(r.client_id) || !r.clients) continue;
    seen.add(r.client_id);
    out.push({
      client_id: r.client_id,
      venue_or_client_name: r.clients.venue_or_client_name ?? "",
      contact_name: r.clients.contact_name ?? "",
      email: r.clients.email ?? "",
      phone: r.clients.phone ?? "",
      venue_postcode: r.venue_postcode ?? "",
      address: r.address ?? "",
      category: r.category,
      fee_pounds: (((r.fee_pence ?? 0) - (r.fuel_charge_pence ?? 0)) / 100).toFixed(2),
    });
  }
  return out;
}
